// Client Activity Tab for Provider Dashboard - Activity Logging & Conversation Monitoring
import React, { useState } from 'react';
import {
  Activity,
  Filter,
  Search,
  CheckCircle,
  Bell,
  ChevronDown,
  Heart,
  AlertTriangle,
  Calendar,
  FileText,
  X
} from 'lucide-react';
import { useActivity } from '../../../contexts/ActivityContext';
import { useAuth } from '../../../contexts/AuthContext';
import ActivityFeed from '../../activity/ActivityFeed';
import ConversationDetailModal from '../../activity/ConversationDetailModal';
import FirebaseActivityService from '../../../services/firebase/FirebaseActivityService';
import { ActivityType, ActivityPriority } from '../../../types/activity';

const ClientActivityTab: React.FC = () => {
  const { userProfile } = useAuth();
  const {
    activities,
    selectedActivity,
    selectedConversation,
    dailySummary,
    unreadCount,
    filters,
    loading,
    setFilters,
    selectActivity,
    markAsRead,
    markAllAsRead
  } = useActivity();

  const [showFilters, setShowFilters] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriorities, setSelectedPriorities] = useState<ActivityPriority[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<ActivityType[]>([]);

  const activityService = FirebaseActivityService.getInstance();

  // Compute today's stats directly from loaded activities (more reliable than dailyActivitySummaries)
  const todayStr = new Date().toISOString().split('T')[0];
  const todayActivities = activities.filter(a => {
    const actDate = a.timestamp instanceof Date ? a.timestamp : new Date(a.timestamp);
    return actDate.toISOString().split('T')[0] === todayStr;
  });
  const todayCount = todayActivities.length;
  const todayNeedsAttention = todayActivities.filter(a => a.priority === 'crisis' || a.priority === 'high').length;
  const todayHopeConversations = todayActivities.filter(a => a.activityType === 'hope_conversation').length;
  const todayFormsSubmitted = todayActivities.filter(a => a.activityType === 'form_submitted').length;

  // Apply filters
  const handleApplyFilters = () => {
    setFilters({
      ...filters,
      searchQuery: searchQuery || undefined,
      priorities: selectedPriorities.length > 0 ? selectedPriorities : undefined,
      activityTypes: selectedTypes.length > 0 ? selectedTypes : undefined
    });
    setShowFilters(false);
  };

  // Clear filters
  const handleClearFilters = () => {
    setSearchQuery('');
    setSelectedPriorities([]);
    setSelectedTypes([]);
    setFilters({});
  };

  // Handle save notes for conversation
  const handleSaveNotes = async (notes: string) => {
    if (!selectedConversation || !userProfile) return;

    await activityService.addProviderNotes(
      selectedConversation.id,
      notes,
      userProfile.uid || '',
      userProfile.displayName || 'Provider'
    );
  };

  // Toggle priority filter
  const togglePriority = (priority: ActivityPriority) => {
    setSelectedPriorities((prev) =>
      prev.includes(priority)
        ? prev.filter((p) => p !== priority)
        : [...prev, priority]
    );
  };

  // Toggle type filter
  const toggleType = (type: ActivityType) => {
    setSelectedTypes((prev) =>
      prev.includes(type)
        ? prev.filter((t) => t !== type)
        : [...prev, type]
    );
  };

  // Check if any filters are active
  const hasActiveFilters =
    searchQuery ||
    selectedPriorities.length > 0 ||
    selectedTypes.length > 0;

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <Activity size={24} color="#3b82f6" />
          <div>
            <h1 style={styles.title}>Client Activity</h1>
            <p style={styles.subtitle}>
              Monitor client interactions and Hope AI conversations
            </p>
          </div>
        </div>

        <div style={styles.headerActions}>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              style={styles.markAllReadButton}
              aria-label={`Mark all ${unreadCount} activities as read`}
            >
              <CheckCircle size={16} />
              Mark all read ({unreadCount})
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div style={styles.summaryCards}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryIcon}>
            <Bell size={20} color="#3b82f6" />
          </div>
          <div style={styles.summaryContent}>
            <span style={styles.summaryValue}>
              {todayCount}
            </span>
            <span style={styles.summaryLabel}>Today's Activities</span>
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, backgroundColor: 'rgba(220, 53, 69, 0.1)' }}>
            <AlertTriangle size={20} color="#dc3545" />
          </div>
          <div style={styles.summaryContent}>
            <span style={styles.summaryValue}>
              {todayNeedsAttention}
            </span>
            <span style={styles.summaryLabel}>Needs Attention</span>
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, backgroundColor: 'rgba(236, 72, 153, 0.1)' }}>
            <Heart size={20} color="#ec4899" />
          </div>
          <div style={styles.summaryContent}>
            <span style={styles.summaryValue}>
              {todayHopeConversations}
            </span>
            <span style={styles.summaryLabel}>Hope Conversations</span>
          </div>
        </div>

        <div style={styles.summaryCard}>
          <div style={{ ...styles.summaryIcon, backgroundColor: 'rgba(34, 197, 94, 0.1)' }}>
            <FileText size={20} color="#22c55e" />
          </div>
          <div style={styles.summaryContent}>
            <span style={styles.summaryValue}>
              {todayFormsSubmitted}
            </span>
            <span style={styles.summaryLabel}>Forms Submitted</span>
          </div>
        </div>
      </div>

      {/* Search and Filter Bar */}
      <div style={styles.filterBar}>
        <div style={styles.searchContainer}>
          <Search size={18} color="#64748b" />
          <input
            type="text"
            placeholder="Search activities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
            style={styles.searchInput}
            aria-label="Search activities"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={styles.clearSearchButton}
              aria-label="Clear search"
            >
              <X size={16} />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          style={{
            ...styles.filterButton,
            ...(hasActiveFilters ? styles.filterButtonActive : {})
          }}
          aria-expanded={showFilters}
          aria-label="Toggle filters"
        >
          <Filter size={16} />
          Filters
          {hasActiveFilters && (
            <span style={styles.filterBadge}>
              {selectedPriorities.length + selectedTypes.length}
            </span>
          )}
          <ChevronDown
            size={16}
            style={{
              transform: showFilters ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s'
            }}
          />
        </button>

        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            style={styles.clearFiltersButton}
            aria-label="Clear all filters"
          >
            Clear all
          </button>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div style={styles.filterPanel}>
          <div style={styles.filterSection}>
            <h4 style={styles.filterSectionTitle}>Priority</h4>
            <div style={styles.filterOptions}>
              {(['crisis', 'high', 'moderate', 'routine'] as ActivityPriority[]).map(
                (priority) => (
                  <button
                    key={priority}
                    onClick={() => togglePriority(priority)}
                    style={{
                      ...styles.filterOption,
                      ...(selectedPriorities.includes(priority)
                        ? styles.filterOptionSelected
                        : {})
                    }}
                  >
                    <span
                      style={{
                        ...styles.priorityDot,
                        backgroundColor:
                          priority === 'crisis'
                            ? '#dc3545'
                            : priority === 'high'
                            ? '#fd7e14'
                            : priority === 'moderate'
                            ? '#ffc107'
                            : '#64748b'
                      }}
                    />
                    {priority.charAt(0).toUpperCase() + priority.slice(1)}
                  </button>
                )
              )}
            </div>
          </div>

          <div style={styles.filterSection}>
            <h4 style={styles.filterSectionTitle}>Activity Type</h4>
            <div style={styles.filterOptions}>
              {[
                { type: 'hope_conversation' as ActivityType, label: 'Hope Conversations' },
                { type: 'form_submitted' as ActivityType, label: 'Forms' },
                { type: 'appointment_scheduled' as ActivityType, label: 'Appointments' },
                { type: 'message_sent' as ActivityType, label: 'Messages' },
                { type: 'crisis_detected' as ActivityType, label: 'Crisis Alerts' }
              ].map(({ type, label }) => (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  style={{
                    ...styles.filterOption,
                    ...(selectedTypes.includes(type)
                      ? styles.filterOptionSelected
                      : {})
                  }}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div style={styles.filterActions}>
            <button onClick={handleApplyFilters} style={styles.applyFiltersButton}>
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Activity Feed */}
      <div style={styles.feedContainer}>
        <ActivityFeed
          activities={activities}
          onSelectActivity={selectActivity}
          onMarkAsRead={markAsRead}
          loading={loading}
        />
      </div>

      {/* Conversation Detail Modal */}
      {selectedConversation && (
        <ConversationDetailModal
          conversation={selectedConversation}
          onClose={() => selectActivity(null)}
          onSaveNotes={handleSaveNotes}
        />
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#0f172a',
    borderRadius: '12px',
    overflow: 'hidden'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '20px 24px',
    borderBottom: '1px solid #334155'
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px'
  },
  title: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  subtitle: {
    margin: '4px 0 0 0',
    fontSize: '13px',
    color: '#64748b'
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  markAllReadButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    backgroundColor: 'transparent',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '13px',
    cursor: 'pointer'
  },
  summaryCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    gap: '12px',
    padding: '16px 24px'
  },
  summaryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '14px 16px',
    backgroundColor: '#1e293b',
    borderRadius: '10px'
  },
  summaryIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    backgroundColor: 'rgba(59, 130, 246, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  summaryContent: {
    display: 'flex',
    flexDirection: 'column'
  },
  summaryValue: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#f8fafc'
  },
  summaryLabel: {
    fontSize: '12px',
    color: '#64748b'
  },
  filterBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '0 24px 16px'
  },
  searchContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 14px',
    backgroundColor: '#1e293b',
    borderRadius: '8px',
    border: '1px solid #334155'
  },
  searchInput: {
    flex: 1,
    background: 'none',
    border: 'none',
    color: '#f8fafc',
    fontSize: '14px',
    outline: 'none'
  },
  clearSearchButton: {
    background: 'none',
    border: 'none',
    padding: '4px',
    cursor: 'pointer',
    color: '#64748b',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  filterButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 14px',
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '8px',
    color: '#94a3b8',
    fontSize: '13px',
    cursor: 'pointer'
  },
  filterButtonActive: {
    borderColor: '#3b82f6',
    color: '#3b82f6'
  },
  filterBadge: {
    backgroundColor: '#3b82f6',
    color: '#fff',
    fontSize: '11px',
    fontWeight: '600',
    padding: '2px 6px',
    borderRadius: '10px'
  },
  clearFiltersButton: {
    background: 'none',
    border: 'none',
    color: '#64748b',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '8px'
  },
  filterPanel: {
    padding: '16px 24px',
    backgroundColor: '#1e293b',
    borderTop: '1px solid #334155',
    borderBottom: '1px solid #334155'
  },
  filterSection: {
    marginBottom: '16px'
  },
  filterSectionTitle: {
    margin: '0 0 10px 0',
    fontSize: '12px',
    fontWeight: '600',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  filterOptions: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px'
  },
  filterOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 12px',
    backgroundColor: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '20px',
    color: '#94a3b8',
    fontSize: '12px',
    cursor: 'pointer'
  },
  filterOptionSelected: {
    backgroundColor: 'rgba(59, 130, 246, 0.15)',
    borderColor: '#3b82f6',
    color: '#3b82f6'
  },
  priorityDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%'
  },
  filterActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '16px'
  },
  applyFiltersButton: {
    padding: '10px 20px',
    backgroundColor: '#3b82f6',
    border: 'none',
    borderRadius: '8px',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  feedContainer: {
    flex: 1,
    overflow: 'auto'
  }
};

export default ClientActivityTab;
